import React from "react";
import { Box, Container, Stack, Grid, Skeleton } from "@mui/material";

export default function ProjectDetailsSkeleton() {
  // Mock array representing the dynamic gallery structure (excluding the hero)
  const skeletonGallery = Array.from({ length: 4 });

  return (
    <Box
      sx={{
        // bgcolor: "#020202",
        color: "#F5F5F7",
        minHeight: "100vh",
        pb: 15,
        "& *": { direction: "rtl" },
      }}
    >
      {/* Header and Title Skeleton Section */}
      <Container maxWidth="xl" sx={{ pt: { xs: 15, md: 20 }, pb: 8 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems="flex-end"
          spacing={6}
        >
          {/* Right Area: Category and Main Heading Skeleton */}
          <Box sx={{ width: "100%", maxWidth: 800 }}>
            {/* Category pill skeleton */}
            <Skeleton
              variant="rounded"
              width={140}
              height={32}
              animation="wave"
              sx={{ bgcolor: "rgba(255,255,255,0.05)", borderRadius: "100px", mb: 4 }}
            />

            {/* Title text skeleton - dynamic multiline visual mapping */}
            <Stack spacing={1}>
              <Skeleton
                variant="text"
                width="75%"
                height={80}
                animation="wave"
                sx={{ bgcolor: "rgba(255,255,255,0.05)" }}
              />
              <Skeleton
                variant="text"
                width="40%"
                height={80}
                animation="wave"
                sx={{ bgcolor: "rgba(255,255,255,0.05)" }}
              />
            </Stack>

            {/* Launch action button skeleton */}
            <Skeleton
              variant="rounded"
              width={130}
              height={40}
              animation="wave"
              sx={{ bgcolor: "rgba(255,255,255,0.05)", borderRadius: "50px", mt: 4 }}
            />
          </Box>

          {/* Left Area: Main narrative description block skeleton */}
          <Box sx={{ width: "100%", maxWidth: 500, pb: 2 }}>
            <Stack spacing={1}>
              <Skeleton variant="text" width="100%" height={24} animation="wave" sx={{ bgcolor: "rgba(255,255,255,0.04)" }} />
              <Skeleton variant="text" width="95%" height={24} animation="wave" sx={{ bgcolor: "rgba(255,255,255,0.04)" }} />
              <Skeleton variant="text" width="98%" height={24} animation="wave" sx={{ bgcolor: "rgba(255,255,255,0.04)" }} />
              <Skeleton variant="text" width="85%" height={24} animation="wave" sx={{ bgcolor: "rgba(255,255,255,0.04)" }} />
            </Stack>
          </Box>
        </Stack>
      </Container>

      {/* Hero Container Visual Skeleton Aspect-Ratio Mirror */}
      <Container maxWidth="xl" sx={{ mb: 10 }}>
        <Skeleton
          variant="rectangular"
          width="100%"
          height="100%"
          animation="wave"
          sx={{
            height: { xs: "50vh", md: "75vh" },
            borderRadius: "24px",
            bgcolor: "rgba(255,255,255,0.03)",
          }}
        />
      </Container>

      {/* Structured Specifications Grid Layout Skeleton */}
      <Container maxWidth="xl" sx={{ mb: { xs: 15, md: 25 } }}>
        <Box
          sx={{
            borderTop: "1px solid rgba(255,255,255,0.1)",
            borderBottom: "1px solid rgba(255,255,255,0.1)",
            py: 4,
          }}
        >
          <Grid container spacing={4}>
            {/* Meta Block 1: Client Spec */}
            <Grid
              size={{ xs: 6, md: 3 }}
              sx={{
                borderLeft: { md: "1px solid rgba(255,255,255,0.05)" },
                px: { md: 3 },
              }}
            >
              <Skeleton variant="text" width={50} height={18} animation="wave" sx={{ bgcolor: "rgba(255,255,255,0.04)", mb: 1 }} />
              <Skeleton variant="text" width={120} height={24} animation="wave" sx={{ bgcolor: "rgba(255,255,255,0.05)" }} />
            </Grid>

            {/* Meta Block 2: Timeline Year Spec */}
            <Grid
              size={{ xs: 6, md: 3 }}
              sx={{
                borderLeft: { md: "1px solid rgba(255,255,255,0.05)" },
                px: { md: 3 },
              }}
            >
              <Skeleton variant="text" width={60} height={18} animation="wave" sx={{ bgcolor: "rgba(255,255,255,0.04)", mb: 1 }} />
              <Skeleton variant="text" width={80} height={24} animation="wave" sx={{ bgcolor: "rgba(255,255,255,0.05)" }} />
            </Grid>

            {/* Meta Block 3: Assignment Role Spec */}
            <Grid
              size={{ xs: 12, md: 3 }}
              sx={{
                borderLeft: { md: "1px solid rgba(255,255,255,0.05)" },
                px: { md: 3 },
              }}
            >
              <Skeleton variant="text" width={60} height={18} animation="wave" sx={{ bgcolor: "rgba(255,255,255,0.04)", mb: 1 }} />
              <Skeleton variant="text" width={150} height={24} animation="wave" sx={{ bgcolor: "rgba(255,255,255,0.05)" }} />
            </Grid>

            {/* Meta Block 4: Architectural Tech Chips */}
            <Grid size={{ xs: 12, md: 3 }} sx={{ px: { md: 3 } }}>
              <Skeleton variant="text" width={80} height={18} animation="wave" sx={{ bgcolor: "rgba(255,255,255,0.04)", mb: 1.5 }} />
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {Array.from({ length: 4 })?.map((_, idx) => (
                  <Skeleton
                    key={idx}
                    variant="rounded"
                    width={65}
                    height={24}
                    animation="wave"
                    sx={{ bgcolor: "rgba(255,255,255,0.03)", borderRadius: "16px" }}
                  />
                ))}
              </Stack>
            </Grid>
          </Grid>
        </Box>
      </Container>

      {/* Split-Screen Technical Breakdown Architecture Skeleton */}
      <Container maxWidth="xl" sx={{ mb: 20 }}>
        <Grid container spacing={8}>
          {/* Content Narrative Panel Column */}
          <Grid size={{ xs: 12, lg: 4 }}>
            <Stack spacing={5}>
              {Array.from({ length: 2 })?.map((_, index) => (
                <Box key={index}>
                  <Skeleton
                    variant="text"
                    width={110}
                    height={28}
                    animation="wave"
                    sx={{ bgcolor: "rgba(255,255,255,0.05)", mb: 1.5 }}
                  />
                  <Stack spacing={1}>
                    <Skeleton variant="text" width="100%" height={20} animation="wave" sx={{ bgcolor: "rgba(255,255,255,0.04)" }} />
                    <Skeleton variant="text" width="96%" height={20} animation="wave" sx={{ bgcolor: "rgba(255,255,255,0.04)" }} />
                    <Skeleton variant="text" width="98%" height={20} animation="wave" sx={{ bgcolor: "rgba(255,255,255,0.04)" }} />
                    <Skeleton variant="text" width="40%" height={20} animation="wave" sx={{ bgcolor: "rgba(255,255,255,0.04)" }} />
                  </Stack>
                </Box>
              ))}
            </Stack>
          </Grid>

          {/* Project Media Mock Gallery Column */}
          <Grid size={{ xs: 12, lg: 8 }}>
            <Grid container spacing={3}>
              {skeletonGallery?.map((_, index) => (
                <Grid size={{ xs: 12, sm: 6 }} key={index}>
                  <Box
                    sx={{
                      width: "100%",
                      aspectRatio: "4/3",
                      borderRadius: "20px",
                      bgcolor: "#0A0A0A",
                      border: "1px solid rgba(255,255,255,0.05)",
                      p: 1.5,
                    }}
                  >
                    <Skeleton
                      variant="rectangular"
                      width="100%"
                      height="100%"
                      animation="wave"
                      sx={{ bgcolor: "rgba(255,255,255,0.03)", borderRadius: "12px" }}
                    />
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}